// webapp/src/AppHeader.js
import React, { useState, useRef, useEffect } from 'react';
import { APP_BASENAME } from './appNavigation';
import { Link } from 'react-router-dom';
import { FiUser, FiLogOut, FiChevronDown, FiShield } from 'react-icons/fi';
import { ADMIN_SUPABASE_USER_ID } from './constants';
import LanguageSelector from './LanguageSelector';
import { useI18n } from './i18n';
import { trackEvent } from './analytics';
import logo from './logo.svg';
import { useGuestCopy } from './guestCopy';

function AppHeader({ currentUser, onSignOut }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const menuButtonRef = useRef(null);
  const { t } = useI18n();
  const guestCopy = useGuestCopy();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const closeOnEscape = event => {
      if (event.key === 'Escape') { setShowDropdown(false); menuButtonRef.current?.focus(); }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [showDropdown]);

  const handleUserMenuToggle = () => {
    const willOpen = !showDropdown;
    setShowDropdown(willOpen);
    trackEvent(willOpen ? 'profile_menu_open' : 'profile_menu_close', {
      source: 'app_header',
    });
  };

  const handleProfileNavigation = () => {
    trackEvent('profile_navigation_click', {
      source: 'app_header',
      destination: 'profile_settings',
    });
    setShowDropdown(false);
  };

  const handleAdminNavigation = () => {
    trackEvent('admin_navigation_click', {
      source: 'app_header',
    });
    setShowDropdown(false);
  };

  return (
    <header className="app-header">
      <div className="header-logo">
        <Link to="/dashboard" className="brand" aria-label="InstantTranscriber dashboard">
          <img src={logo} className="brand__icon" alt="" />
          <span className="brand__text">
            <span className="brand__name">
              Instant<span className="brand__name--grad">Transcriber</span>
            </span>
            <span className="brand__tagline">{t('common.brandTagline')}</span>
          </span>
        </Link>
      </div>
      
      {currentUser?.is_anonymous ? (
        <div className="auth-controls"><LanguageSelector compact /><Link to="/login" className="secondary-button">{t("common.signIn")}</Link><Link to="/signup" className="primary-button">{guestCopy.save}</Link></div>
      ) : currentUser ? (
        <div className="user-controls" ref={dropdownRef}>
          <LanguageSelector compact />
          <button type="button"
            className="user-info-button"
            onClick={handleUserMenuToggle}
            ref={menuButtonRef}
            aria-label={t('workspace.accountMenu')}
            aria-expanded={showDropdown}
            aria-controls={showDropdown ? 'workspace-account-menu' : undefined}
          >
            <span className="user-info">
              <FiUser />
              <span>{currentUser.email}</span>
              <FiChevronDown size={16} />
            </span>
          </button>
          
          {showDropdown && (
            <div className="user-dropdown" id="workspace-account-menu">
              <Link to="/dashboard/profile" className="dropdown-item" onClick={handleProfileNavigation}>
                <FiUser />
                <span>{t('common.profile')}</span>
              </Link>
              {currentUser?.id === ADMIN_SUPABASE_USER_ID && (
                APP_BASENAME ? (
                  <a href="https://app.instanttranscriber.com/admin" target="_top" className="dropdown-item" onClick={handleAdminNavigation}>
                    <FiShield /><span>{t('common.admin')}</span>
                  </a>
                ) : (
                  <Link to="/admin" className="dropdown-item" onClick={handleAdminNavigation}>
                    <FiShield /><span>{t('common.admin')}</span>
                  </Link>
                )
              )}
              <button className="dropdown-item" onClick={onSignOut}>
                <FiLogOut />
                <span>{t('common.signOut')}</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="auth-controls">
          <LanguageSelector compact />
          <Link to="/login" className="secondary-button">
            {t('common.signIn')}
          </Link>
          <Link to="/signup" className="primary-button">
            {t('common.signUp')}
          </Link>
        </div>
      )}
    </header>
  );
}

export default AppHeader;
